import React, { type RefObject, useCallback, useMemo } from "react";
import type { Text as NativeText } from "react-native";
import Markdown from "react-native-markdown-renderer";
import { useAppTheme } from "../../useAppTheme";
import { extractMath } from "./extract-math";
import { normalizeHighlightMarks } from "./normalize-highlight-marks";
import { normalizeUnderline } from "./normalize-underline";
import { getRenderRules } from "./render-rules";
import rules from "./rules";
import { type CustomStyle, getStyles, type TextAlign } from "./styles";
import { useMarkdownIt } from "./use-markdown-it";

export type { CustomStyle, TextAlign } from "./styles";

export type MarkdownRendererProps = {
	text?: string;
	fontSize?: number;
	textAlign?: TextAlign;
	variant?: keyof typeof rules;
	customStyles?: CustomStyle;
	useCustomLink?: boolean;
	overrideRules?: Record<string, unknown>;
	handleClickInsertableText?: (choiceId: string) => void;
	handleHighlightedTextPress?: (contextId?: string) => void;
	highlightedTextRef?: RefObject<NativeText | null>;
	appFont?: string;
	disableInsertableText?: boolean;
	paragraphMarginVertical?: number;
	renderBlank?: (index: number) => React.ReactNode;
};

const RenderMarkdown = ({
	text,
	fontSize = 14,
	textAlign = "left",
	variant = "explanation",
	customStyles,
	useCustomLink = true,
	overrideRules,
	handleClickInsertableText,
	handleHighlightedTextPress,
	highlightedTextRef,
	appFont,
	disableInsertableText = false,
	paragraphMarginVertical,
	renderBlank,
}: MarkdownRendererProps) => {
	const theme = useAppTheme();
	const md = useMarkdownIt(variant);

	const styles = useMemo(
		() => getStyles(theme, fontSize, textAlign, customStyles, appFont),
		[theme, fontSize, textAlign, customStyles, appFont],
	);

	const handleInsert = useCallback(
		(content: string) => {
			const choiceId = content.slice(1, content.indexOf("+"));
			handleClickInsertableText?.(choiceId);
		},
		[handleClickInsertableText],
	);

	const source = text ?? "";
	const { cleaned: processedContent, mathMap } = useMemo(
		() => extractMath(normalizeUnderline(normalizeHighlightMarks(source))),
		[source],
	);

	const renderRules = useMemo(
		() =>
			getRenderRules({
				fontSize,
				textAlign,
				theme,
				customStyles,
				useCustomLink,
				onInsert: handleInsert,
				onHighlightPress: handleHighlightedTextPress,
				highlightRef: highlightedTextRef,
				appFont,
				disableInsertableText,
				paragraphMarginVertical,
				mathMap,
				renderBlank,
			}),
		[
			fontSize,
			textAlign,
			theme,
			customStyles,
			useCustomLink,
			handleInsert,
			handleHighlightedTextPress,
			highlightedTextRef,
			appFont,
			disableInsertableText,
			paragraphMarginVertical,
			mathMap,
			renderBlank,
		],
	);

	return (
		<Markdown
			style={styles}
			markdownit={md}
			rules={{
				...renderRules,
				...(overrideRules as Record<string, never> | undefined),
			}}
		>
			{processedContent}
		</Markdown>
	);
};

export default RenderMarkdown;
